import React, { useState, useMemo } from 'react';
import '../styles/OneWayInStyle.scss';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ONE_WAY_IN } from '..';
import { useCluster } from '../../cluster';
import useDocumentTitle from 'src/hooks/use-document-title';
import { useSupportEmail } from '../../../hooks/use-support-email';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import OneWayInOpportunityTable from '../opportunity-table/components/OneWayInOpportunityTable';
import useOneWayInList from '../hooks/use-one-way-in-list';
import { useLocalStorage } from 'usehooks-ts';
import { DataTableV3LocalStoragePrefixes } from 'src/components/DataTableV3/constants/localStorage';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import { OneWayInOpportunitySimpleV2Dto, OneWayInStatus } from 'src/api/v2';
import { useSearchParams } from 'react-router-dom';
import BatchArchivingModal from '../components/batch-archiving/BatchArchivingModal';
import OpportunityBoard from '../opportunity-board/components/OpportunityBoard';
import DeliveredServicesStatCard from '../components/DeliveredServicesStatCard/DeliveredServicesStatCard';
import {
  Alert,
  Button,
  Heading,
  Icon,
  Input,
  Tabs,
  ToggleGroup,
  useDebounceCallback,
  Link as ClLink,
} from '@innovationnorway/cl-react';
import useGeoNorge from '../hooks/use-geonorge';
import DeliverServicesModal from '../opportunity-board/components/DeliverServicesModal';
import RecommendServicesModal from '../opportunity-board/components/RecommendServicesModal';
export enum TabNames {
  Active = 'active',
  Archive = 'archive',
}
const OneWayInOpportunities: React.FC = () => {
  const { cluster } = useCluster();
  const { email, mailto } = useSupportEmail();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tHelpAndSupport } = useTranslation('onboarding');
  const { getMunicipalityNameByNumber } = useGeoNorge();
  const [localStorageTop] = useLocalStorage(
    `${DataTableV3LocalStoragePrefixes.pageSize}${DataTableV3Ids.OneWayIn.Opportunities}`,
    25,
  );
  const [top, setTop] = React.useState<number>(localStorageTop);
  const [board, setBoard] = useLocalStorage(`opportunities__tableView`, true);
  const {
    oneWayInAllSavedFormsQuery: { data: opportunityList, isLoading, isError },
  } = useOneWayInList();
  const { documentTitle } = useDocumentTitle(tOneWayIn('opportunities'));
  const hasCounties = !!cluster?.oneWayInCounties?.length;
  const hasOpportunities = opportunityList ? opportunityList.length > 0 : false;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRows, setSelectedRows] = useState<OneWayInOpportunitySimpleV2Dto[]>([]);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showRecommendServicesDialog, setShowRecommendServicesDialog] = useState(false);
  const [showDeliverServicesDialog, setShowDeliverServicesDialog] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filteredOpportunities = useMemo(() => {
    const archivedStatuses = [OneWayInStatus.Closed, OneWayInStatus.Forwarded, OneWayInStatus.AutoArchived];
    if (!opportunityList) {
      return {
        openOpportunities: [],
        archivedOpportunities: [],
      };
    }
    const opportunities = opportunityList.reduce(
      (acc, opportunity) => {
        if (
          opportunity.status?.status === undefined ||
          !archivedStatuses.includes(opportunity.status.status)
        ) {
          acc.openOpportunities.push(opportunity);
        } else {
          acc.archivedOpportunities.push(opportunity);
        }
        return acc;
      },
      {
        openOpportunities: [] as OneWayInOpportunitySimpleV2Dto[],
        archivedOpportunities: [] as OneWayInOpportunitySimpleV2Dto[],
      },
    );
    return opportunities;
  }, [opportunityList]);
  const selectedTab = useMemo(() => {
    return searchParams.get('activeTab') || TabNames.Active;
  }, [searchParams]);
  const debouncedhandleFilter = useDebounceCallback((value: string) => {
    setFilterText(value);
  }, 500);
  const boardOpportunities = useMemo(() => {
    if (filterText === '') return opportunityList ?? [];
    if (!opportunityList) return [];
    const lowercaseFilter = filterText.toLocaleLowerCase();
    return opportunityList.filter((opportunity) => {
      return (
        opportunity.title.toLocaleLowerCase().includes(lowercaseFilter) ||
        opportunity.customerName?.toLocaleLowerCase().includes(lowercaseFilter) ||
        opportunity.organizationName?.toLocaleLowerCase().includes(lowercaseFilter) ||
        (getMunicipalityNameByNumber(opportunity.municipality) || '')
          .toLocaleLowerCase()
          .includes(lowercaseFilter)
      );
    });
  }, [filterText, getMunicipalityNameByNumber, opportunityList]);
  const switchView = (value: string) => {
    if (value === 'true') {
      setBoard(true);
    } else {
      setBoard(false);
    }
  };
  return (
    <>
      <DocumentTitle title={documentTitle}>
        <main className="full-width-layout stack">
          <div className="display--flex gap--2 justify-content--space-between align-items--start flex-wrap--wrap">
            <div className="stack">
              <Heading level={1}>{tOneWayIn('opportunities')}</Heading>
              <div className="display--flex gap--2 flex-wrap--wrap">
                <Button
                  variant="secondary"
                  className="how-to-card"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent('openSideMenu', {
                        detail: 'index/commonQuestions/owi/invite',
                      }),
                    )
                  }
                >
                  <Icon className="how-to-card-icon" prefix="fasr" iconName="question" />
                  <span>{tHelpAndSupport('commonQuestions.Invite')}</span>
                  <Icon prefix="fasr" iconName={'arrow-right'} />
                </Button>
                <Button
                  variant="secondary"
                  className="how-to-card"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent('openSideMenu', {
                        detail: 'index/commonQuestions/owi/howTo',
                      }),
                    )
                  }
                >
                  <Icon className="how-to-card-icon" prefix="fasr" iconName="question" />
                  <span>{tOneWayIn('frontpage.howTo.header')}</span>
                  <Icon prefix="fasr" iconName={'arrow-right'} />
                </Button>
              </div>
              <div className="display--flex gap--2 flex-wrap--wrap">
                <ToggleGroup
                  className="flex-basis--40"
                  data-toggle-group="Velg visning"
                  value={board.toString()}
                  onChange={switchView}
                >
                  <ToggleGroup.Item value="false">Tabell</ToggleGroup.Item>
                  <ToggleGroup.Item value="true">Tavle</ToggleGroup.Item>
                </ToggleGroup>
                {board && (
                  <Input
                    placeholder="Filtrer på navn, tittel, bedrift..."
                    onChange={(e) => debouncedhandleFilter(e.currentTarget.value)}
                    type="text"
                  />
                )}
              </div>
            </div>
            <div className="justify-self--end">
              <DeliveredServicesStatCard opportunityList={filteredOpportunities.openOpportunities || []} />
            </div>
          </div>

          {!isLoading && !hasOpportunities && (
            <div className="margin-top--4">
              <Icon prefix="fasr" iconName="folder-open" size="xl" />
              <Heading level={2} className={'margin-top--3'}>
                {tOneWayIn('noOpportunitiesFoundTitle')}
              </Heading>
              <p>{tOneWayIn('noOpportunitiesFoundDescription')}</p>

              <p>
                <Trans
                  t={tOneWayIn}
                  i18nKey="noOpportunitiesFoundContactInfo"
                  values={{ email: 'support@enveiinn.no' }}
                  components={{ '1': <ClLink href={mailto}>{email}</ClLink> }}
                >
                  Hvis du har spørsmål eller innspill så ta gjerne kontakt på support@enveiinn.no
                </Trans>
              </p>
            </div>
          )}

          {!isLoading && isError && (
            <Alert data-color="danger" role="alert">
              {tOneWayIn('errorCouldNotGetAllForms')}
            </Alert>
          )}

          {board ? (
            <OpportunityBoard opportunityList={boardOpportunities} isLoading={isLoading} />
          ) : (
            <>
              <Tabs value={selectedTab}>
                <Tabs.List>
                  <Tabs.Tab
                    key={TabNames.Active}
                    value={TabNames.Active}
                    onClick={() => {
                      setSearchParams(`activeTab=${TabNames.Active}`, {
                        replace: true,
                      });
                    }}
                  >
                    {tOneWayIn('tabs.active')}
                  </Tabs.Tab>
                  <Tabs.Tab
                    key={TabNames.Archive}
                    value={TabNames.Archive}
                    onClick={() => {
                      setSearchParams(`activeTab=${TabNames.Archive}`, {
                        replace: true,
                      });
                    }}
                  >
                    {tOneWayIn('tabs.archive')}
                  </Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value={TabNames.Active}>
                  <OneWayInOpportunityTable
                    isLoading={isLoading}
                    opportunities={filteredOpportunities.openOpportunities}
                    paginationListeners={{
                      onSetPageSize: (pageSize) => setTop(pageSize),
                    }}
                    pageSize={top}
                    overridePaginationTotalNumber={filteredOpportunities.openOpportunities.length}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    setShowDeliverServicesDialog={setShowDeliverServicesDialog}
                    setShowArchiveDialog={setShowArchiveDialog}
                    setShowRecommendServicesDialog={setShowRecommendServicesDialog}
                  />
                </Tabs.Panel>
                <Tabs.Panel value={TabNames.Archive}>
                  <OneWayInOpportunityTable
                    isLoading={isLoading}
                    opportunities={filteredOpportunities.archivedOpportunities}
                    paginationListeners={{
                      onSetPageSize: (pageSize) => setTop(pageSize),
                    }}
                    pageSize={top}
                    overridePaginationTotalNumber={filteredOpportunities.archivedOpportunities.length}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                  />
                </Tabs.Panel>
              </Tabs>
            </>
          )}
          <BatchArchivingModal
            showDialog={showArchiveDialog}
            setShowDialog={setShowArchiveDialog}
            selectedRows={selectedRows}
          />
          <DeliverServicesModal
            dialogOpen={showDeliverServicesDialog}
            onClose={() => setShowDeliverServicesDialog(false)}
            opportunities={selectedRows}
          />
          <RecommendServicesModal
            dialogOpen={showRecommendServicesDialog}
            onClose={() => setShowRecommendServicesDialog(false)}
            opportunities={selectedRows}
          />
          {hasCounties && (
            <div className="margin-top--8">
              <p>
                {tOneWayIn('countyWarning')}{' '}
                <Link className="link--underline" to={ONE_WAY_IN.COUNTY}>
                  {tOneWayIn('goToCountyView')}
                </Link>
                .
              </p>
            </div>
          )}
        </main>
      </DocumentTitle>
    </>
  );
};
export default OneWayInOpportunities;
